import { useMemo, useState } from "react";
import { _Product_liquid, _Variant_liquid } from "./_types";
import { SingleSelectedConfigurableProduct } from "./product";
import { SearchIcon } from "./icons";
import { ProductItemCheckbox } from "./product-item-checkbox";
import { isDefaultTitle } from "./variant-helpers";
import { useGlobalProducts } from "./global-stores";
import { useVariantDetailsModal } from "./use-variant-details-modal";

interface Props {
  productList: _Product_liquid[];
  gridColumnsDesktop?: string;
  gridColumnsMobile?: string;
  selectedProduct?: SingleSelectedConfigurableProduct;
  selectedVariant?: _Variant_liquid;
  displayPrice?: boolean;
  renderingStrategy?: "per_product" | "per_variant";
  shouldShowProductTitle?: boolean;
  quantityToAdd?: number;
  stepNumber: number;
  openInfoInNewTab?: boolean;
  onProductSelect?: (
    stepNumber: number,
    product: _Product_liquid,
    variant: _Variant_liquid,
    quantityToAdd: number
  ) => void;
  onProductUnselect?: (stepNumber: number) => void;
}

const DESKTOP_COLS_CLASSES = {
  "3": "md:grid-cols-3",
  "4": "md:grid-cols-4",
  "5": "md:grid-cols-5",
  "6": "md:grid-cols-6",
};
const MOBILE_COLS_CLASSES = {
  "1": "grid-cols-1",
  "2": "grid-cols-2",
  "3": "grid-cols-3",
  "4": "grid-cols-4",
};

const getGridClasses = (desktopCols?: string, mobileCols?: string) => {
  const desktop = DESKTOP_COLS_CLASSES[desktopCols || "5"] || "md:grid-cols-5";
  const mobile = MOBILE_COLS_CLASSES[mobileCols || "2"] || "grid-cols-2";
  return `grid ${desktop} ${mobile} gap-4`;
};

export const ProductGroupCheckboxCard = ({
  productList,
  gridColumnsDesktop,
  gridColumnsMobile,
  selectedProduct,
  selectedVariant,
  displayPrice,
  renderingStrategy = "per_product",
  shouldShowProductTitle = true,
  quantityToAdd,
  stepNumber,
  openInfoInNewTab = false,
  onProductSelect,
  onProductUnselect,
}: Props) => {
  const [loadingVariantId, setLoadingVariantId] = useState<number | null>(null);

  const { getAsyncProduct } = useGlobalProducts(({ getAsyncProduct }) => ({
    getAsyncProduct,
  }));
  const { toggleModal, setProductData } = useVariantDetailsModal();

  const productsToShow = useMemo(() => {
    if (renderingStrategy === "per_variant") {
      return [
        {
          id: 0,
          variants: productList.flatMap((p) =>
            p.variants.map((v) => ({
              ...v,
              _parentProduct: p,
            }))
          ),
        },
      ];
    } else {
      return productList;
    }
  }, [productList, renderingStrategy]);

  return (
    <div className={"flex max-h-[500px] flex-col gap-8 overflow-y-auto md:max-h-none"}>
      {productsToShow.map((product, idx) => {
        return (
          <div className={"flex flex-col gap-2"} key={product.id + idx}>
            {shouldShowProductTitle && (
              <p className={"sticky left-0 right-0 top-0 z-10 bg-white py-2 text-sm"}>
                {product.title}
              </p>
            )}
            <div className={getGridClasses(gridColumnsDesktop, gridColumnsMobile)}>
              {product.variants.map((variant) => {
                const parentProduct =
                  renderingStrategy === "per_variant" ? (variant as any)._parentProduct : product;
                const variantId = variant.id;
                const isChecked = selectedProduct?.selectedVariant?.id === variantId;
                const isLoading = loadingVariantId === variantId;

                return (
                  <div
                    className={"relative"}
                    key={`step-${stepNumber}-${parentProduct.id}-${variantId}`}
                  >
                    <ProductItemCheckbox
                      product={parentProduct}
                      variant={variant}
                      displayPrice={displayPrice}
                      gridColumnsDesktop={gridColumnsDesktop}
                      quantityToAdd={quantityToAdd}
                      checked={isChecked}
                      showProductTitle={renderingStrategy === "per_variant"}
                      onClick={() => {
                        if (!isChecked) {
                          onProductSelect &&
                            onProductSelect(
                              stepNumber,
                              parentProduct,
                              variant,
                              Number(quantityToAdd) || 1
                            );
                        } else {
                          onProductUnselect && onProductUnselect(stepNumber);
                        }
                      }}
                    />
                    <button
                      title={"More Info about this product"}
                      className={`absolute right-1 top-1 rounded-full bg-[#DFEFFE] px-2 py-2 text-sm text-gray-500 md:right-1 md:top-1 md:px-1 md:py-1 ${isLoading ? "cursor-wait opacity-50" : ""}`}
                      aria-label={"More Info"}
                      disabled={isLoading}
                      onClick={async () => {
                        try {
                          if (openInfoInNewTab) {
                            // Open product in new tab
                            const productUrl = `/products/${parentProduct.handle}?variant=${variant.id}`;
                            window.open(productUrl, "_blank", "noopener,noreferrer");
                            return;
                          } else {
                            setLoadingVariantId(variantId);
                            const productData = await getAsyncProduct(parentProduct.handle);
                            const selectedVariant = productData?.variants.find(
                              (v) => v.id === variant.id
                            );
                            const variant_modal_image = selectedVariant?.metafields?.custom?.[
                              "variant_modal_image"
                            ] as string | undefined;
                            const variant_description = selectedVariant?.metafields?.custom?.[
                              "variant_description"
                            ] as string | undefined;
                            const composition = selectedVariant?.metafields?.custom?.[
                              "composition"
                            ] as string | undefined;
                            const durability = selectedVariant?.metafields?.custom?.[
                              "durability"
                            ] as string | undefined;
                            const care_instructions = selectedVariant?.metafields?.custom?.[
                              "care_instructions"
                            ] as string | undefined;

                            const displayTitle = isDefaultTitle(variant)
                              ? parentProduct.title
                              : `${parentProduct.title} ${variant.title}`.trim();

                            setProductData({
                              title: displayTitle,
                              imageSrc:
                                variant_modal_image ||
                                selectedVariant?.featured_media?.src ||
                                parentProduct.featured_image ||
                                "",
                              imageAlt: variant.title || parentProduct.title,
                              description: variant_description || "",
                              composition: composition || "",
                              durability: durability || "",
                              careInstructions: care_instructions || "",
                            });
                            toggleModal(true);
                          }
                        } finally {
                          setLoadingVariantId(null);
                        }
                      }}
                    >
                      <SearchIcon className={`h-3 w-3 text-[#0071E3]`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
